import { flow } from 'fp-ts/function';
import { each, filter, findIndex, first, get, identity, isEmpty, isNil, over, size } from 'lodash/fp';
import pt from 'prop-types';
import { Component } from 'react';
import styled from 'styled-components';
import { IconButton, IconName } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { COLOR_WHITE } from '../../../components/commander-ui/constants';

const SwitchButtonWrapper = styled.div`
    top: 8px;
    left: 8px;
    position: absolute;
`;

const CameraInputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const MessageWrapper = styled.span`
    font-size: 36px;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0px;
    left: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
`;

const SwitchButton = styled(IconButton)`
    background: ${COLOR_WHITE};
`;
SwitchButton.defaultProps = {
    icon: IconName.SwitchVideo,
    variant: 'ghost',
};

const VideoPlayer = styled.video`
    width: 100%;
    height: auto;
`;

const Canvas = styled.canvas`
    width: 100%;
    height: auto;
    position: absolute;
    top: 0px;
    left: 0px;
    opacity: 0;
    ${(props) =>
        (props as $TSFixMe).isVisible &&
        `
        opacity: 1;
    `}
`;

const factory = ({
    VideoPlayerComponent = VideoPlayer,
    CanvasComponent = Canvas,
    SwitchButtonComponent = SwitchButton,
    MessageWrapperComponent = MessageWrapper,
    SwitchButtonWrapperComponent = SwitchButtonWrapper,
    CameraInputContainerComponent = CameraInputContainer,
} = {}) =>
    class CameraImageInput extends Component {
        static propTypes = {
            isOpened: pt.bool,
            isFetching: pt.bool,
        };

        canvas: $TSFixMe;
        videoPlayer: $TSFixMe;

        state = {
            availableDevices: [],
            currentDeviceId: null,
            currentStream: null,
            initialized: false,
            supported: !!window.navigator.mediaDevices,
        };

        componentDidMount() {
            if (window.navigator.mediaDevices) {
                window.navigator.mediaDevices
                    .enumerateDevices()
                    .then(this.initializeDevices)
                    .then(this.startStream)
                    .catch(this.showError);
            }
        }

        componentDidUpdate(prevProps: $TSFixMe, prevState: $TSFixMe) {
            const prevIsReady = this.mapStateToIsReady(prevState);
            const isReady = this.mapStateToIsReady(this.state);
            if (prevIsReady !== isReady) {
                (this.props as $TSFixMe).onIsReadyChanged(isReady);
            }
            if (prevProps.isOpened && !(this.props as $TSFixMe).isOpened) {
                this.stopStream();
            }
            if (!prevProps.isOpened && (this.props as $TSFixMe).isOpened) {
                this.startStream();
            }
        }

        componentWillUnmount() {
            this.stopStream();
        }

        playerRef = (c: $TSFixMe) => (this.videoPlayer = c);

        canvasRef = (c: $TSFixMe) => (this.canvas = c);

        mapStateToIsReady = ({ currentStream, availableDevices, supported, initialized }: $TSFixMe) =>
            !isEmpty(availableDevices) && !isNil(currentStream) && initialized && supported;

        initializeDevices = flow(
            filter({ kind: 'videoinput' }),
            over([identity, first]),
            ([availableDevices, firstDevice]) =>
                this.setState({ availableDevices, currentDeviceId: get('deviceId')(firstDevice) })
        );

        handleDeviceSwitch = () => {
            const { availableDevices, currentDeviceId } = this.state;
            // @ts-expect-error $TSFixMeError: Argument of type '{ deviceId: null; }' is not assignable to parameter of type 'ValueIterateeCustom<{ deviceId: unknown; ...
            const index = findIndex({ deviceId: currentDeviceId })(availableDevices);
            const nextDeviceIndex = (index + 1) % size(availableDevices);
            this.setState(
                {
                    currentDeviceId: get('deviceId')(availableDevices[nextDeviceIndex]),
                },
                this.startStream
            );
        };

        startStream = () => {
            this.videoPlayer.srcObject = null;
            this.setState({
                initialized: false,
            });
            const { availableDevices, currentDeviceId } = this.state;
            if (isEmpty(availableDevices) || isNil(currentDeviceId) || isNil(this.videoPlayer)) {
                this.showError();
                return;
            }

            this.stopStream();
            window.navigator.mediaDevices
                .getUserMedia({
                    video: {
                        deviceId: { exact: currentDeviceId },
                    },
                })
                .then((stream) => {
                    this.videoPlayer.srcObject = stream;
                    this.videoPlayer.onloadedmetadata = () => {
                        this.videoPlayer.play();
                    };
                    this.videoPlayer.oncanplay = () => {
                        this.setState({
                            initialized: true,
                        });
                    };

                    this.setState({
                        currentStream: stream,
                    });
                })
                .catch(this.showError);
        };

        stopStream = () => {
            const { currentStream } = this.state;
            if (currentStream) {
                each((track) => (track as $TSFixMe).stop())((currentStream as $TSFixMe).getTracks());
            }
        };

        showError = () => this.setState({ initialized: true });

        capture = () => {
            this.canvas.width = this.videoPlayer.videoWidth;
            this.canvas.height = this.videoPlayer.videoHeight;

            this.canvas.getContext('2d').drawImage(this.videoPlayer, 0, 0);

            return this.canvas.toDataURL();
        };

        renderMessage = () => {
            const { availableDevices, initialized, currentStream, supported } = this.state;
            const hasDevicePermission = !isNil(currentStream);
            const hasDevice = !isEmpty(availableDevices);

            if (!supported) {
                return (
                    <MessageWrapperComponent>
                        <LocalizedText textKey={TextKey.FeatureNotSupportedByBrowser} />
                    </MessageWrapperComponent>
                );
            }

            if (!initialized) {
                return (
                    <MessageWrapperComponent>
                        <LocalizedText textKey={TextKey.FindingYourCamera} />
                    </MessageWrapperComponent>
                );
            }

            if (!hasDevice) {
                return (
                    <MessageWrapperComponent>
                        <LocalizedText textKey={TextKey.NoDevicesAvailable} />
                    </MessageWrapperComponent>
                );
            }

            if (!hasDevicePermission) {
                return (
                    <MessageWrapperComponent>
                        <LocalizedText textKey={TextKey.NotAllowedToAccessDevice} />
                    </MessageWrapperComponent>
                );
            }
        };

        render() {
            const { availableDevices } = this.state;
            const hasMoreDevices = size(availableDevices) > 1;

            return (
                <CameraInputContainerComponent>
                    <VideoPlayerComponent ref={this.playerRef} autoPlay playsInline muted />
                    <CanvasComponent
                        ref={this.canvasRef}
                        // @ts-expect-error $TSFixMeError: No overload matches this call.
                        isVisible={(this.props as $TSFixMe).isFetching || !(this.props as $TSFixMe).isOpened}
                    />
                    {this.renderMessage()}
                    {hasMoreDevices && (
                        <SwitchButtonWrapperComponent>
                            {/* @ts-expect-error $TSFixMeError: No overload matches this call. */}
                            <SwitchButtonComponent onClick={this.handleDeviceSwitch} />
                        </SwitchButtonWrapperComponent>
                    )}
                </CameraInputContainerComponent>
            );
        }
    };

export const CameraImageInput = factory();
